/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import { SkeletonText, SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { Internet } from "../InternetData";
import {
  P2P_LEG_LABELS,
  P2P_LEG_ROLES,
  p2pAddressingFromApi,
  p2pLegOf,
} from "../internetAddressing";

export interface InternetViewModalProps {
  isOpen: boolean;
  isLoading: boolean;
  internet: Internet | null;
  onClose: () => void;
}

export const InternetViewModal: React.FC<InternetViewModalProps> = ({
  isOpen,
  isLoading,
  internet,
  onClose,
}) => {
  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="Internet Enterprise Service Details"
      customWidth="max-w-5xl"
      overflowY="auto"
    >
      {isLoading ? (
        <div className="space-y-6">
          {[...Array(3)].map((_, idx) => (
            <div key={idx} className="grid grid-cols-2 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i}>
                  <SkeletonBlock h={20} w="30%" className="mb-3" />
                  <SkeletonText lines={1} lineHeight={14} />
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        internet && (
          <div className="space-y-6">
            {/* Section 2: Port Information */}
            {internet.kontrolEdgePort && (
              <GenericCard
                isCollapsible={false}
                headerContent={
                    <h3 className="text-lg font-semibold text-semantic-text-primary">
                        Port Information
                    </h3>
                }
                variant="primary"
                shadow="flat"
                hasBorder={true}
              >
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <GenericInput
                      label="Sub ID"
                      value={internet.kontrolEdgePort.subid ?? ""}
                      readOnly
                    />
                    <GenericInput
                      label="Port Name"
                      value={internet.kontrolEdgePort.name ?? ""}
                      readOnly
                    />
                    <GenericInput
                      label="Hardware ID"
                      value={internet.kontrolEdgePort.hardwareId ?? ""}
                      readOnly
                    />
                    <GenericInput
                      label="Capacity (Mbps)"
                      value={String(internet.kontrolEdgePort.capacityMbps ?? "")}
                      readOnly
                    />
                    {internet.kontrolEdgePort.datacenter && (
                      <>
                        <div className="col-span-2">
                          <label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
                            Datacenter Information
                          </label>
                          <div className="grid grid-cols-2 gap-4 p-4 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
                            <GenericInput
                              label="Datacenter ID"
                              value={internet.kontrolEdgePort.datacenter.datacenterId ?? ""}
                              readOnly
                            />
                            <GenericInput
                              label="Name"
                              value={internet.kontrolEdgePort.datacenter.name ?? ""}
                              readOnly
                            />
                            <GenericInput
                              label="Display Name"
                              value={internet.kontrolEdgePort.datacenter.displayName ?? ""}
                              readOnly
                            />
                            <GenericInput
                              label="City"
                              value={internet.kontrolEdgePort.datacenter.city ?? ""}
                              readOnly
                            />
                          </div>
                        </div>
                      </>
                    )}
                    {internet.kontrolEdgePort.hardware && (
                      <>
                        <div className="col-span-2">
                          <label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
                            Hardware Information
                          </label>
                          <div className="grid grid-cols-2 gap-4 p-4 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
                            <GenericInput
                              label="Hardware ID"
                              value={String(internet.kontrolEdgePort.hardware.id ?? "")}
                              readOnly
                            />
                            <GenericInput
                              label="Hostname"
                              value={internet.kontrolEdgePort.hardware.hostname ?? ""}
                              readOnly
                            />
                            <GenericInput
                              label="Model"
                              value={internet.kontrolEdgePort.hardware.model ?? ""}
                              readOnly
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </GenericCard>
            )}

            {/* Section 1: General Information */}
            <GenericCard
              isCollapsible={false}
              headerContent={<h3 className="text-lg font-semibold text-semantic-text-primary">General Information</h3>}
              variant="primary"
              shadow="flat"
              hasBorder={true}
            >
              <div className="p-4">
                <div className="grid grid-cols-2 gap-4">
                  <GenericInput
                    label="Name"
                    value={internet.name ?? ""}
                    readOnly
                  />
                  <GenericInput
                    label="Subscription ID"
                    value={internet.subscriptionId ?? ""}
                    readOnly
                  />
                  <GenericInput
                    label="Mbps"
                    value={String(internet.mbps ?? "")}
                    readOnly
                  />
                  <GenericInput
                    label="VLAN"
                    value={String(internet.vlan ?? "")}
                    readOnly
                  />
                  <GenericInput
                    label="VLAN (Secondary)"
                    value={String(internet.p2pAddressing?.vlanSecondary ?? "")}
                    readOnly
                  />
                  <GenericInput
                    label="Automatic VLAN"
                    value={internet.automaticVlan ? "Yes" : "No"}
                    readOnly
                  />
                  <GenericInput
                    label="Redundancy"
                    value={internet.redundancy ? "Yes" : "No"}
                    readOnly
                  />
                  <GenericInput
                    label="Country Code"
                    value={internet.countryCode ?? ""}
                    readOnly
                  />
                  <GenericInput
                    label="Routing"
                    value={internet.routing ?? ""}
                    readOnly
                  />
                  <GenericInput
                    label="Status"
                    value={internet.isDraft ? "Draft" : "Active"}
                    readOnly
                  />
                  <GenericInput
                    label="Created At"
                    value={internet.createdAt ? new Date(internet.createdAt).toLocaleString() : ""}
                    readOnly
                  />
                  {P2P_LEG_ROLES.map((role) => {
                    const leg = p2pLegOf(p2pAddressingFromApi(internet.p2pAddressing), role);
                    if (!leg) return null;
                    return (
                      <div key={role} className="col-span-2">
                        <label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
                          P2P Addressing — {P2P_LEG_LABELS[role]}
                        </label>
                        <div className="grid grid-cols-4 gap-4 p-4 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
                          <GenericInput label="Network" value={leg.network} readOnly />
                          <GenericInput label="Gateway" value={leg.gateway} readOnly />
                          <GenericInput label="Peer IP" value={leg.peerIp} readOnly />
                          <GenericInput label="Mask" value={String(leg.mask ?? "")} readOnly />
                        </div>
                      </div>
                    );
                  })}
                  {internet.bgpConfig && (
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-semantic-text-secondary mb-2 block">
                        BGP Neighbors
                      </label>
                      <div className="grid grid-cols-2 gap-4 p-4 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
                        <GenericInput
                          label="Primary neighbor IP"
                          value={internet.bgpConfig.neighborIpAddress ?? ""}
                          readOnly
                        />
                        <GenericInput
                          label="Primary peering type"
                          value={internet.bgpConfig.peeringType ?? ""}
                          readOnly
                        />
                        <GenericInput
                          label="Secondary neighbor IP"
                          value={internet.bgpConfig.neighborIpAddressSecondary ?? ""}
                          readOnly
                        />
                        <GenericInput
                          label="Secondary peering type"
                          value={internet.bgpConfig.peeringTypeSecondary ?? ""}
                          readOnly
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </GenericCard>

          </div>
        )
      )}

      <div className="flex justify-end mt-6">
        <GenericButton label="Close" variant="secondary" onClick={onClose} />
      </div>
    </GenericModal>
  );
};

